import { useCallback, useEffect, useState } from 'react';

import type { DestinationsListStateUpdate } from 'hooks/use-destinations-list-state';
import { DestinationContentTypes as DestinationContentTypesConst } from 'constants/destination-content-types';
import type { DestinationContentTypes } from 'typings/destinations/DestinationContentTypeEnum';

export const CONTENT_TYPE_OPTIONS: {
    value: DestinationContentTypes | null;
    label: string;
}[] = [
    { value: null, label: 'All' },
    { value: DestinationContentTypesConst.TAGLINE, label: 'Tagline' },
    { value: DestinationContentTypesConst.DESCRIPTION, label: 'Description' },
    {
        value: DestinationContentTypesConst.GOOD_TO_KNOW,
        label: 'Good to know',
    },
    { value: DestinationContentTypesConst.HIGHLIGHT, label: 'Highlight' },
    { value: DestinationContentTypesConst.ITINERARY, label: 'Itinerary' },
];

export type UseDestinationFiltersParams = Readonly<{
    query: string;
    source: number | null;
    status: number | null;
    geoType: number | null;
    contentType?: number;
    onlyWithoutPhotos: boolean;
    hasGuide: boolean;
    allHighlightsHaveImages: boolean | null;
    setDebouncedQuery: (query: string) => void;
    updateState: (state: DestinationsListStateUpdate) => void;
}>;

/**
 * Owns all local UI state, derived values, and change handlers for the
 * Destinations filter bar, keeping the `DestinationFilters` component focused
 * on markup.
 */
export function useDestinationFilters({
    query,
    source,
    status,
    geoType,
    contentType,
    onlyWithoutPhotos,
    hasGuide,
    allHighlightsHaveImages,
    setDebouncedQuery,
    updateState,
}: UseDestinationFiltersParams) {
    const [searchValue, setSearchValue] = useState(query);
    const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

    useEffect(() => {
        setSearchValue(query);
    }, [query]);

    const activeSecondaryFilterCount = [
        contentType !== undefined,
        onlyWithoutPhotos,
        hasGuide,
        allHighlightsHaveImages !== null,
    ].filter(Boolean).length;

    const hasAnyActiveFilter =
        Boolean(query) ||
        source !== null ||
        status !== null ||
        geoType !== null ||
        activeSecondaryFilterCount > 0;

    const contentTypeLabel =
        CONTENT_TYPE_OPTIONS.find((opt) => opt.value === contentType)?.label ??
        '';

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchValue(value);
            setDebouncedQuery(value);
            updateState({ page: 1 });
        },
        [setDebouncedQuery, updateState],
    );

    const handleStatusChange = useCallback(
        (value: number | null) => updateState({ status: value, page: 1 }),
        [updateState],
    );

    const handleSourceChange = useCallback(
        (value: number | null) => updateState({ source: value, page: 1 }),
        [updateState],
    );

    const handleGeoTypeChange = useCallback(
        (value: number | null) => updateState({ geoType: value, page: 1 }),
        [updateState],
    );

    const handleContentTypeChange = useCallback(
        (value: number | null) =>
            updateState({
                contentType: value,
                hasContent: value === null ? undefined : true,
                page: 1,
            }),
        [updateState],
    );

    const handleHasContentChange = useCallback(
        (checked: boolean) => updateState({ hasContent: checked, page: 1 }),
        [updateState],
    );

    const handleHasGuideChange = useCallback(
        (checked: boolean) => updateState({ hasGuide: checked, page: 1 }),
        [updateState],
    );

    const handleOnlyWithoutPhotosChange = useCallback(
        (checked: boolean) =>
            updateState({ onlyWithoutPhotos: checked, page: 1 }),
        [updateState],
    );

    const handleAllHighlightsHaveImagesChange = useCallback(
        (value: boolean | null) =>
            updateState({ allHighlightsHaveImages: value, page: 1 }),
        [updateState],
    );

    const handleClearAll = useCallback(() => {
        setSearchValue('');
        setDebouncedQuery('');
        updateState({
            query: '',
            source: null,
            status: null,
            geoType: null,
            contentType: null,
            onlyWithoutPhotos: false,
            hasGuide: false,
            allHighlightsHaveImages: null,
            page: 1,
        });
    }, [setDebouncedQuery, updateState]);

    return {
        searchValue,
        isMoreFiltersOpen,
        setIsMoreFiltersOpen,
        activeSecondaryFilterCount,
        hasAnyActiveFilter,
        contentTypeLabel,
        handleSearchChange,
        handleStatusChange,
        handleSourceChange,
        handleGeoTypeChange,
        handleContentTypeChange,
        handleHasContentChange,
        handleHasGuideChange,
        handleOnlyWithoutPhotosChange,
        handleAllHighlightsHaveImagesChange,
        handleClearAll,
    };
}
